import type { DiceView, MinigameMove } from '@wyn/contracts';
import { useEffect, useState } from 'react';

const FACE = '⚀⚁⚂⚃⚄⚅';
const faces = (p: [number, number] | null) => (p ? FACE[p[0] - 1]! + FACE[p[1] - 1]! : '⚀⚀');

/**
 * The Dice Cellar. The server rolls (provably fair) and plays the house's turn in the same reply;
 * the table replays the house's rolls one by one so you can watch them land.
 */
export function DiceGame({ view, commitment, busy, onMove }: { view: DiceView; commitment: string; busy: boolean; onMove: (m: MinigameMove) => void }) {
  const [shown, setShown] = useState(view.houseRolls.length);
  useEffect(() => {
    setShown(0);
    if (view.houseRolls.length === 0) return;
    const id = setInterval(() => setShown((n) => (n >= view.houseRolls.length ? n : n + 1)), 650);
    return () => clearInterval(id);
  }, [view]);
  const houseRolling = shown < view.houseRolls.length;
  const dice = view.houseRolls.length ? view.houseRolls[Math.max(0, shown - 1)]! : view.last;

  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-dim">
        You need {view.goal}; the house needs only {view.houseGoal}. Roll to build the pot and bank it before a one shows. A single one loses the pot; snake eyes loses everything.
        {view.palms > 0 ? ` You can palm a die ${view.palms} more time${view.palms === 1 ? '' : 's'}, but the croupier spots it ${Math.round(view.palmRisk * 100)}% of the time and the game is lost.` : ''}
        {view.rides > 0 ? ` You let it ride: this game pays ${view.winnings} or nothing.` : ' Win, and you may let it ride once: one more game for double or nothing.'}
      </p>
      <div className="flex justify-between font-mono">
        <span>You <b data-testid="dice-me">{view.me}</b></span>
        <span>Pot <b className="text-gilt" data-testid="dice-pot">{view.pot}</b></span>
        <span>House <b>{houseRolling ? '…' : view.house}</b></span>
      </div>
      <div className="text-center text-6xl leading-tight tracking-[0.2em]" aria-label="Dice">{faces(dice ?? null)}</div>
      <p className="text-center text-sm" role="status">{houseRolling ? 'The house rolls…' : view.say}</p>
      {view.awaiting === 'ride' ? (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn py-3" disabled={busy} onClick={() => onMove({ type: 'collect' })}>Collect {view.winnings}</button>
          <button type="button" className="btn py-3" disabled={busy} onClick={() => onMove({ type: 'ride' })}>Let it ride for {view.winnings * 2}</button>
        </div>
      ) : view.awaiting === 'palm' ? (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn" disabled={busy || houseRolling} onClick={() => onMove({ type: 'palm' })}>Palm the die</button>
          <button type="button" className="btn" disabled={busy || houseRolling} onClick={() => onMove({ type: 'pass' })}>Curse and pass</button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn py-3" disabled={busy || houseRolling || view.awaiting === 'over'} onClick={() => onMove({ type: 'roll' })}>🎲 Roll</button>
          <button type="button" className="btn py-3" disabled={busy || houseRolling || view.awaiting === 'over' || view.pot === 0} onClick={() => onMove({ type: 'bank' })}>Bank</button>
        </div>
      )}
      <p className="break-all font-mono text-xs text-ink-faint">House seed commitment: {commitment}</p>
    </div>
  );
}
